import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import AppIcon from "../components/AppIcon";
import SchoolBrand from "../components/SchoolBrand";
import StatusToast from "../components/StatusToast";
import "../styles/dashboard.css";

const SECTION_OPTIONS = ["A", "B", "C", "D", "E"];

const initialHomework = {
  title: "",
  description: "",
  subject: "",
  className: "",
  section: "",
  dueDate: "",
  attachmentUrl: "",
  attachmentName: "",
  attachmentMimeType: ""
};
const initialLeaveForm = {
  leaveType: "casual",
  startDate: "",
  endDate: "",
  reason: ""
};

const toMs = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const getReadableTeacherError = (err, fallbackMessage) => {
  const message = String(err?.response?.data?.message || "").trim();
  if (!message) return fallbackMessage;

  const lowered = message.toLowerCase();
  if (lowered.includes("session")) {
    return "Session issue detected. Please re-login if this continues.";
  }
  if (lowered.includes("not authorized")) {
    return "You are not authorized for this action. Please log in again.";
  }
  if (lowered.includes("missing fields")) {
    return "Please complete all required fields before submitting.";
  }
  if (lowered.includes("class") && lowered.includes("section")) {
    return "Please select a valid class and section before continuing.";
  }

  return message;
};

const TeacherDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [classes, setClasses] = useState([]);
  const [homework, setHomework] = useState([]);
  const [notices, setNotices] = useState([]);
  const [events, setEvents] = useState([]);
  const [timetable, setTimetable] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [teacherProfile, setTeacherProfile] = useState(null);
  const [seenMeta, setSeenMeta] = useState({ homework: 0, notices: 0, events: 0 });
  const [form, setForm] = useState(initialHomework);
  const [saving, setSaving] = useState(false);
  const [reviewingSubmissionId, setReviewingSubmissionId] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);
  const [markingNotificationRead, setMarkingNotificationRead] = useState(false);
  const [leaveForm, setLeaveForm] = useState(initialLeaveForm);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [savingLeave, setSavingLeave] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [topUserOpen, setTopUserOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [showProfilePassword, setShowProfilePassword] = useState(false);
  const topUserRef = useRef(null);

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const storageKeys = useMemo(() => {
    const keyBase = user?.id || user?.email || "teacher";
    return {
      homework: `seen_teacher_homework_${keyBase}`,
      notices: `seen_teacher_notices_${keyBase}`,
      events: `seen_teacher_events_${keyBase}`
    };
  }, [user]);

  const latestHomeworkMs = useMemo(
    () => homework.reduce((max, item) => Math.max(max, toMs(item.createdAt)), 0),
    [homework]
  );

  const latestNoticeMs = useMemo(
    () => notices.reduce((max, item) => Math.max(max, toMs(item.createdAt)), 0),
    [notices]
  );
  const latestEventMs = useMemo(
    () => events.reduce((max, item) => Math.max(max, toMs(item.createdAt)), 0),
    [events]
  );

  const unreadHomeworkCount = useMemo(
    () => homework.filter((item) => toMs(item.createdAt) > (seenMeta.homework || 0)).length,
    [homework, seenMeta.homework]
  );

  const unreadNoticeCount = useMemo(
    () => notices.filter((item) => toMs(item.createdAt) > (seenMeta.notices || 0)).length,
    [notices, seenMeta.notices]
  );
  const unreadEventCount = useMemo(
    () => events.filter((item) => toMs(item.createdAt) > (seenMeta.events || 0)).length,
    [events, seenMeta.events]
  );
  const pendingReviewCount = useMemo(
    () => submissions.filter((item) => item.status !== "reviewed").length,
    [submissions]
  );
  const upcomingEventsCount = useMemo(
    () => events.filter((item) => {
      if (!item.eventDate) return false;
      const dt = new Date(item.eventDate);
      return !Number.isNaN(dt.getTime()) && dt.getTime() >= Date.now();
    }).length,
    [events]
  );
  const recentHomework = useMemo(() => homework.slice(0, 4), [homework]);
  const recentNotices = useMemo(() => notices.slice(0, 4), [notices]);

  const markSeen = (type) => {
    const seenAt = type === "homework"
      ? Math.max(Date.now(), latestHomeworkMs)
      : type === "events"
        ? Math.max(Date.now(), latestEventMs)
        : Math.max(Date.now(), latestNoticeMs);
    const key = type === "homework" ? storageKeys.homework : type === "events" ? storageKeys.events : storageKeys.notices;
    setSeenMeta((prev) => ({ ...prev, [type]: seenAt }));
    localStorage.setItem(key, String(seenAt));
  };

  const load = async () => {
    const [classesRes, homeworkRes, noticesRes, eventsRes, profileRes, timetableRes, submissionsRes, notifRes, leaveRes] = await Promise.all([
      api.get("/portal/teacher/classes"),
      api.get("/portal/teacher/homework"),
      api.get("/portal/teacher/notices"),
      api.get("/portal/teacher/events"),
      api.get("/portal/teacher/profile").catch(() => ({ data: { teacher: null } })),
      api.get("/portal/teacher/timetable").catch(() => ({ data: { entries: [] } })),
      api.get("/portal/teacher/homework-submissions").catch(() => ({ data: { submissions: [] } })),
      api.get("/portal/notifications").catch(() => ({ data: { notifications: [], unreadCount: 0 } })),
      api.get("/portal/teacher/leave-requests").catch(() => ({ data: { requests: [] } }))
    ]);
    const cls = classesRes.data.classes || [];
    setClasses(cls);
    setHomework(homeworkRes.data.homework || []);
    setNotices(noticesRes.data.notices || []);
    setEvents(eventsRes.data.events || []);
    setTimetable(timetableRes.data.entries || []);
    setSubmissions(submissionsRes.data.submissions || []);
    setNotifications(notifRes.data.notifications || []);
    setNotificationUnreadCount(Number(notifRes.data.unreadCount || 0));
    setLeaveRequests(leaveRes.data.requests || []);
    setTeacherProfile(profileRes.data.teacher || null);
    if (cls.length > 0) {
      setForm((prev) => (prev.className ? prev : { ...prev, className: cls[0].className, section: cls[0].section || "" }));
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("adminToken");
    localStorage.removeItem("teacherToken");
    localStorage.removeItem("parentToken");
    localStorage.removeItem("role");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const copyToClipboard = async (value, label) => {
    const text = String(value || "").trim();
    if (!text || text === "-") {
      setError(`${label} not available to copy.`);
      return;
    }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const fallback = document.createElement("textarea");
        fallback.value = text;
        fallback.setAttribute("readonly", "");
        fallback.style.position = "absolute";
        fallback.style.left = "-9999px";
        document.body.appendChild(fallback);
        fallback.select();
        document.execCommand("copy");
        document.body.removeChild(fallback);
      }
      setSuccess(`${label} copied.`);
    } catch {
      setError(`Failed to copy ${label.toLowerCase()}.`);
    }
  };

  useEffect(() => {
    let token = localStorage.getItem("teacherToken");
    if (!token) {
      const fallback = localStorage.getItem("token");
      const role = localStorage.getItem("role");
      if (fallback && role === "teacher") {
        token = fallback;
        localStorage.setItem("teacherToken", fallback);
      }
    }
    if (!token) {
      navigate("/login");
      return;
    }

    let polling = false;
    const init = async () => {
      if (polling) return;
      polling = true;
      try {
        await load();
      } catch (err) {
        if ([401, 403].includes(err.response?.status)) {
          setError("Session issue detected. Please re-login if this continues.");
        } else {
          setError(getReadableTeacherError(err, "Failed to load teacher portal"));
        }
      } finally {
        polling = false;
        setLoading(false);
      }
    };
    init();
    const timer = setInterval(() => init(), 30000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    const homeworkSeen = Number(localStorage.getItem(storageKeys.homework) || 0);
    const noticesSeen = Number(localStorage.getItem(storageKeys.notices) || 0);
    const eventsSeen = Number(localStorage.getItem(storageKeys.events) || 0);
    setSeenMeta({ homework: homeworkSeen, notices: noticesSeen, events: eventsSeen });
  }, [storageKeys]);

  useEffect(() => {
    if (!error && !success) return undefined;
    const timer = setTimeout(() => {
      setError("");
      setSuccess("");
    }, 3000);
    return () => clearTimeout(timer);
  }, [error, success]);

  useEffect(() => {
    if (activeTab === "homework-list") markSeen("homework");
    if (activeTab === "notices") markSeen("notices");
    if (activeTab === "events") markSeen("events");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (!topUserRef.current) return;
      if (!topUserRef.current.contains(event.target)) {
        setTopUserOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const handleAssign = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api.post("/portal/teacher/homework", {
        title: form.title,
        description: form.description,
        subject: form.subject,
        className: form.className,
        section: form.section,
        attachmentUrl: form.attachmentUrl,
        attachmentName: form.attachmentName,
        attachmentMimeType: form.attachmentMimeType,
        dueDate: form.dueDate ? `${form.dueDate}T23:59:59.999Z` : undefined
      });
      setForm((prev) => ({ ...initialHomework, className: prev.className, section: prev.section }));
      setActiveTab("homework-list");
      await load();
      setSuccess("Homework assigned successfully.");
    } catch (err) {
      setError(getReadableTeacherError(err, "Failed to assign homework"));
    } finally {
      setSaving(false);
    }
  };

  const reviewSubmission = async (id, status) => {
    setReviewingSubmissionId(String(id));
    setError("");
    try {
      await api.post(`/portal/teacher/homework-submissions/${id}/review`, { status });
      await load();
      setSuccess("Submission updated successfully.");
    } catch (err) {
      setError(getReadableTeacherError(err, "Failed to update submission"));
    } finally {
      setReviewingSubmissionId("");
    }
  };

  const submitLeaveRequest = async (e) => {
    e.preventDefault();
    setSavingLeave(true);
    setError("");
    try {
      await api.post("/portal/teacher/leave-requests", leaveForm);
      setLeaveForm(initialLeaveForm);
      await load();
      setSuccess("Leave request submitted.");
    } catch (err) {
      setError(getReadableTeacherError(err, "Failed to submit leave request"));
    } finally {
      setSavingLeave(false);
    }
  };

  const markNotificationsRead = async () => {
    setMarkingNotificationRead(true);
    setError("");
    try {
      await api.post("/portal/notifications/mark-read");
      setNotifications((prev) => prev.map((item) => ({ ...item, unread: false })));
      setNotificationUnreadCount(0);
    } catch (err) {
      setError(getReadableTeacherError(err, "Failed to mark notifications as read"));
    } finally {
      setMarkingNotificationRead(false);
    }
  };

  const handleHomeworkFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = [
      "text/plain",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/pdf"
    ];
    if (!allowedTypes.includes(file.type)) {
      setError("Only TXT, DOC, DOCX, and PDF files are allowed.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("File size should be less than 5MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setForm((prev) => ({
        ...prev,
        attachmentUrl: String(reader.result || ""),
        attachmentName: file.name,
        attachmentMimeType: file.type
      }));
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  if (loading) return <div className="dash-root"><p>Loading teacher dashboard...</p></div>;

  const teacherName = teacherProfile?.name || user?.name || "Teacher";
  const teacherRole = "Teacher";
  const teacherPassword = teacherProfile?.loginPassword || "";
  const teacherInitials = teacherName
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className={`dash-root teacher-portal ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}>
      <StatusToast
        message={error || success}
        type={error ? "error" : "success"}
        onClose={() => {
          setError("");
          setSuccess("");
        }}
      />
      {profileModalOpen && (
        <div className="confirm-modal-backdrop" role="presentation">
          <div className="confirm-modal-card profile-modal-card" role="dialog" aria-modal="true" aria-label="Teacher profile">
            <button type="button" className="profile-modal-close" onClick={() => setProfileModalOpen(false)} aria-label="Close profile">
              <AppIcon name="close" />
            </button>
            <div className="profile-modal-head">
              <span className="profile-head-avatar">{teacherInitials || "TR"}</span>
              <div className="profile-head-meta">
                <h3>Teacher Profile</h3>
                <span className="profile-role-badge">{teacherRole}</span>
              </div>
            </div>
            <div className="profile-grid">
              <div className="profile-row"><strong>Name:</strong><span>{teacherName}</span></div>
              <div className="profile-row">
                <strong>Email:</strong>
                <span className="profile-value-wrap">
                  <span>{teacherProfile?.email || user?.email || "-"}</span>
                  <button type="button" className="profile-copy-btn" onClick={() => copyToClipboard(teacherProfile?.email || user?.email, "Email")} title="Copy email">
                    <AppIcon name="copy" />
                  </button>
                </span>
              </div>
              <div className="profile-row"><strong>Role:</strong><span>{teacherRole}</span></div>
              <div className="profile-row">
                <strong>Phone:</strong>
                <span className="profile-value-wrap">
                  <span>{teacherProfile?.phone || "-"}</span>
                  <button type="button" className="profile-copy-btn" onClick={() => copyToClipboard(teacherProfile?.phone, "Phone")} title="Copy phone">
                    <AppIcon name="copy" />
                  </button>
                </span>
              </div>
              <div className="profile-row"><strong>Address:</strong><span>{teacherProfile?.address || "-"}</span></div>
              <div className="profile-row profile-password-row">
                <strong>Password:</strong>
                <span className="profile-value-wrap">
                  <span>{showProfilePassword ? (teacherPassword || "Not set") : "********"}</span>
                <button
                  type="button"
                  className="profile-copy-btn"
                  onClick={() => setShowProfilePassword((prev) => !prev)}
                  title={showProfilePassword ? "Hide password" : "Show password"}
                >
                  <AppIcon name={showProfilePassword ? "eyeOff" : "eye"} />
                </button>
                </span>
              </div>
            </div>
            <div className="confirm-modal-actions">
              <button type="button" onClick={() => setProfileModalOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
      <aside className="dash-sidebar">
        <SchoolBrand />
        <div className="dash-sidebar-inner">
          <nav className="dash-nav">
            <div className="dash-nav-group">
              <button className={activeTab === "overview" ? "dash-nav-btn active" : "dash-nav-btn"} onClick={() => setActiveTab("overview")}>
                <span className="nav-row"><AppIcon name="dashboard" className="nav-icon" /><span className="nav-text">Dashboard</span></span>
              </button>
            </div>
            <div className="dash-nav-group">
              <button className={activeTab === "assign" ? "dash-nav-btn active" : "dash-nav-btn"} onClick={() => setActiveTab("assign")}>
                <span className="nav-row"><AppIcon name="assignment" className="nav-icon" /><span className="nav-text">Assign Homework</span></span>
              </button>
            </div>
            <div className="dash-nav-group">
              <button className={activeTab === "homework-list" ? "dash-nav-btn active" : "dash-nav-btn"} onClick={() => setActiveTab("homework-list")}>
                <span className="nav-row"><AppIcon name="list" className="nav-icon" /><span className="nav-text">Assigned Homework</span>{unreadHomeworkCount > 0 && <span className="dash-badge">{unreadHomeworkCount}</span>}</span>
              </button>
            </div>
            <div className="dash-nav-group">
              <button className={activeTab === "submissions" ? "dash-nav-btn active" : "dash-nav-btn"} onClick={() => setActiveTab("submissions")}>
                <span className="nav-row"><AppIcon name="assignment" className="nav-icon" /><span className="nav-text">Student Submissions</span></span>
              </button>
            </div>
            <div className="dash-nav-group">
              <button className={activeTab === "timetable" ? "dash-nav-btn active" : "dash-nav-btn"} onClick={() => setActiveTab("timetable")}>
                <span className="nav-row"><AppIcon name="list" className="nav-icon" /><span className="nav-text">Class Timetable</span></span>
              </button>
            </div>
            <div className="dash-nav-group">
              <button className={activeTab === "notices" ? "dash-nav-btn active" : "dash-nav-btn"} onClick={() => setActiveTab("notices")}>
                <span className="nav-row"><AppIcon name="notice" className="nav-icon" /><span className="nav-text">Notice Board</span>{unreadNoticeCount > 0 && <span className="dash-badge">{unreadNoticeCount}</span>}</span>
              </button>
            </div>
            <div className="dash-nav-group">
              <button className={activeTab === "events" ? "dash-nav-btn active" : "dash-nav-btn"} onClick={() => setActiveTab("events")}>
                <span className="nav-row"><AppIcon name="history" className="nav-icon" /><span className="nav-text">School Events</span>{unreadEventCount > 0 && <span className="dash-badge">{unreadEventCount}</span>}</span>
              </button>
            </div>
            <div className="dash-nav-group">
              <button className={activeTab === "leave-requests" ? "dash-nav-btn active" : "dash-nav-btn"} onClick={() => setActiveTab("leave-requests")}>
                <span className="nav-row"><AppIcon name="history" className="nav-icon" /><span className="nav-text">My Leave Requests</span></span>
              </button>
            </div>
            <div className="dash-nav-group">
              <button className={activeTab === "notifications" ? "dash-nav-btn active" : "dash-nav-btn"} onClick={() => setActiveTab("notifications")}>
                <span className="nav-row"><AppIcon name="notice" className="nav-icon" /><span className="nav-text">Notifications</span>{notificationUnreadCount > 0 && <span className="dash-badge">{notificationUnreadCount}</span>}</span>
              </button>
            </div>
          </nav>
        </div>
        <button className="dash-logout" onClick={logout}>
          <span className="nav-row"><AppIcon name="logout" className="nav-icon" /><span className="nav-text">Logout</span></span>
        </button>
      </aside>

      <section className="dash-main">
        <header className="dash-topbar">
          <button
            type="button"
            className="sidebar-collapse-toggle"
            onClick={() => setSidebarCollapsed((prev) => !prev)}
            aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <AppIcon name="chevronRight" className={sidebarCollapsed ? "collapse-icon collapsed" : "collapse-icon"} />
          </button>
          <h1 className="dash-top-title admin-top-title">Welcome To MPIPS School Management System Teacher Dashboard</h1>
          <div className="top-user-wrap" ref={topUserRef}>
            <button type="button" className="top-user-btn" onClick={() => setTopUserOpen((prev) => !prev)}>
              <span className="top-user-avatar">{teacherInitials || "TR"}</span>
              <span className="top-user-meta">
                <strong>{teacherName}</strong>
                <small>{teacherRole}</small>
              </span>
              <AppIcon name="chevronRight" className={topUserOpen ? "submenu-arrow open" : "submenu-arrow"} />
            </button>
            {topUserOpen && (
              <div className="top-user-dropdown">
                <button type="button" onClick={() => { setProfileModalOpen(true); setTopUserOpen(false); }}>View Profile</button>
                <button type="button" onClick={logout}>Logout</button>
              </div>
            )}
          </div>
        </header>
        <main className="dash-content">
          <p className="dash-breadcrumb">Home - Teacher</p>
          {activeTab === "overview" && (
            <section>
              <div className="teacher-overview-hero">
                <div className="teacher-overview-profile">
                  <div className="teacher-overview-avatar-wrap">
                    {teacherProfile?.photoUrl ? (
                      <img src={teacherProfile.photoUrl} alt={teacherProfile.name || "Teacher"} className="teacher-overview-avatar" />
                    ) : (
                      <div className="teacher-overview-avatar placeholder"><AppIcon name="teachers" size={28} /></div>
                    )}
                  </div>
                  <div>
                    <h2>{teacherName}</h2>
                    <p>{teacherProfile?.classTeacherOf ? `Class Teacher: ${teacherProfile.classTeacherOf}` : "Teacher Dashboard"}</p>
                    <div className="teacher-overview-tags">
                      <span className="hero-badge">Classes: {classes.length}</span>
                      <span className="hero-badge">Upcoming Events: {upcomingEventsCount}</span>
                    </div>
                  </div>
                </div>
                <div className="teacher-quick-actions">
                  <button type="button" className="table-btn" onClick={() => setActiveTab("assign")}>Assign Homework</button>
                  <button type="button" className="table-btn ghost" onClick={() => setActiveTab("submissions")}>Review Submissions</button>
                  <button type="button" className="table-btn ghost" onClick={() => setActiveTab("leave-requests")}>Leave Requests</button>
                </div>
              </div>
              <div className="teacher-metric-grid">
                <article className="dash-card teacher-metric classes"><p>Classes Assigned</p><h3>{classes.length}</h3></article>
                <article className="dash-card teacher-metric homework"><p>Homework Created</p><h3>{homework.length}</h3></article>
                <article className="dash-card teacher-metric notices"><p>Notices</p><h3>{notices.length}</h3></article>
                <article className="dash-card teacher-metric reviews"><p>Pending Reviews</p><h3>{pendingReviewCount}</h3></article>
              </div>
              <div className="teacher-overview-grid">
                <div className="dash-panel">
                  <div className="table-header-row">
                    <h3 style={{ margin: 0 }}>Recent Homework</h3>
                    <button type="button" className="table-btn ghost" onClick={() => setActiveTab("homework-list")}>View All</button>
                  </div>
                  <div className="notice-feed">
                    {recentHomework.length === 0 && <p>No homework assigned yet.</p>}
                    {recentHomework.map((item) => (
                      <article key={item.id} className="notice-feed-item">
                        <h3>{item.title}</h3>
                        <p>Class {item.className}-{item.section || "NA"} | {item.subject || "General"}</p>
                        <small>{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "-"}</small>
                      </article>
                    ))}
                  </div>
                </div>
                <div className="dash-panel">
                  <div className="table-header-row">
                    <h3 style={{ margin: 0 }}>Latest Notices</h3>
                    <button type="button" className="table-btn ghost" onClick={() => setActiveTab("notices")}>View All</button>
                  </div>
                  <div className="notice-feed">
                    {recentNotices.length === 0 && <p>No notices available.</p>}
                    {recentNotices.map((item) => (
                      <article key={item.id} className="notice-feed-item">
                        <h3>{item.title}</h3>
                        <p>{item.details || "-"}</p>
                        <small>{item.noticeDate ? new Date(item.noticeDate).toLocaleDateString() : "-"}</small>
                      </article>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          )}

          {activeTab === "assign" && (
            <section>
              <h2>Assign Homework</h2>
              <form className="admin-form" onSubmit={handleAssign}>
                <div className="form-field"><label>Title *</label><input value={form.title} onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))} required /></div>
                <div className="form-field"><label>Subject</label><input value={form.subject} onChange={(e) => setForm((prev) => ({ ...prev, subject: e.target.value }))} /></div>
                <div className="form-field"><label>Class *</label><input value={form.className} onChange={(e) => setForm((prev) => ({ ...prev, className: e.target.value }))} list="teacher-class-options" required /></div>
                <datalist id="teacher-class-options">
                  {classes.map((item, index) => <option key={`${item.className}-${index}`} value={item.className} />)}
                </datalist>
                <div className="form-field">
                  <label>Section</label>
                  <select value={form.section} onChange={(e) => setForm((prev) => ({ ...prev, section: e.target.value }))}>
                    <option value="">Select Section</option>
                    {SECTION_OPTIONS.map((section) => <option key={section} value={section}>{section}</option>)}
                  </select>
                </div>
                <div className="form-field"><label>Due Date</label><input type="date" value={form.dueDate} onChange={(e) => setForm((prev) => ({ ...prev, dueDate: e.target.value }))} /></div>
                <div className="form-field">
                  <label>Attachment (TXT/DOC/DOCX/PDF)</label>
                  <input type="file" accept=".txt,.doc,.docx,.pdf,text/plain,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf" onChange={handleHomeworkFile} />
                </div>
                <div className="form-field" style={{ gridColumn: "span 2" }}><label>Description</label><input value={form.description} onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))} /></div>
                <div className="form-field" style={{ gridColumn: "span 2" }}>
                  <label>Selected File</label>
                  <span className="dash-subtitle" style={{ padding: 0 }}>{form.attachmentName || "No file selected"}</span>
                </div>
                <div className="admin-form-actions">
                  <button type="submit" disabled={saving}>{saving ? "Assigning..." : "Assign Homework"}</button>
                </div>
              </form>
            </section>
          )}

          {activeTab === "homework-list" && (
            <section>
              <h2>Homework List</h2>
              <div className="dash-table-wrap">
                <table className="dash-table">
                  <thead>
                    <tr><th>Date</th><th>Title</th><th>Class</th><th>Subject</th><th>Due</th><th>File</th><th>Students</th></tr>
                  </thead>
                  <tbody>
                    {homework.length === 0 && <tr><td colSpan={7}>No homework assigned yet.</td></tr>}
                    {homework.map((item) => (
                      <tr key={item.id}>
                        <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                        <td>{item.title}</td>
                        <td>{item.className} - {item.section || "NA"}</td>
                        <td>{item.subject || "-"}</td>
                        <td>{item.dueDate ? new Date(item.dueDate).toLocaleDateString() : "-"}</td>
                        <td>{item.attachmentUrl ? <a href={item.attachmentUrl} download={item.attachmentName || "homework-file"} target="_blank" rel="noreferrer">{item.attachmentName || "Download"}</a> : "-"}</td>
                        <td>{(item.students || []).length}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {activeTab === "timetable" && (
            <section>
              <h2>Class Timetable</h2>
              <div className="dash-table-wrap">
                <table className="dash-table">
                  <thead><tr><th>Class</th><th>Section</th><th>Day</th><th>Period</th><th>Time</th><th>Subject</th><th>Teacher</th><th>Room</th></tr></thead>
                  <tbody>
                    {timetable.length === 0 && <tr><td colSpan={8}>No timetable entries found.</td></tr>}
                    {timetable.map((row) => (
                      <tr key={row._id}>
                        <td>{row.className}</td>
                        <td>{row.section || "-"}</td>
                        <td>{row.dayOfWeek}</td>
                        <td>{row.period}</td>
                        <td>{row.startTime || "-"} {row.endTime ? `- ${row.endTime}` : ""}</td>
                        <td>{row.subject || "-"}</td>
                        <td>{row.teacherName || "-"}</td>
                        <td>{row.room || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {activeTab === "submissions" && (
            <section>
              <h2>Homework Submissions</h2>
              <div className="dash-table-wrap">
                <table className="dash-table">
                  <thead><tr><th>Date</th><th>Homework</th><th>Student</th><th>Attachment</th><th>Status</th><th>Action</th></tr></thead>
                  <tbody>
                    {submissions.length === 0 && <tr><td colSpan={6}>No submissions yet.</td></tr>}
                    {submissions.map((row) => (
                      <tr key={row._id}>
                        <td>{new Date(row.createdAt).toLocaleDateString()}</td>
                        <td>{row.homework?.title || "-"}</td>
                        <td>{row.student?.name || "-"}</td>
                        <td>{row.attachmentUrl ? <a href={row.attachmentUrl} target="_blank" rel="noreferrer">View</a> : "-"}</td>
                        <td><span className={row.status === "reviewed" ? "status-pill paid" : row.status === "late" ? "status-pill due" : "status-pill pending"}>{row.status}</span></td>
                        <td>
                          <div className="icon-actions">
                            <button type="button" className="icon-action-btn success" disabled={reviewingSubmissionId === String(row._id)} onClick={() => reviewSubmission(row._id, "reviewed")} title="Mark Reviewed">
                              <AppIcon name="check" />
                            </button>
                            <button type="button" className="icon-action-btn warn" disabled={reviewingSubmissionId === String(row._id)} onClick={() => reviewSubmission(row._id, "late")} title="Mark Late">
                              <AppIcon name="close" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {activeTab === "leave-requests" && (
            <section>
              <h2>Leave Requests</h2>
              <div className="dashboard-lower-grid">
                <div className="dash-panel">
                  <h3>Apply Leave</h3>
                  <form className="admin-form" onSubmit={submitLeaveRequest}>
                    <div className="form-field">
                      <label>Leave Type</label>
                      <select value={leaveForm.leaveType} onChange={(e) => setLeaveForm((prev) => ({ ...prev, leaveType: e.target.value }))}>
                        <option value="casual">Casual</option>
                        <option value="sick">Sick</option>
                        <option value="emergency">Emergency</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="form-field"><label>Start Date *</label><input type="date" value={leaveForm.startDate} onChange={(e) => setLeaveForm((prev) => ({ ...prev, startDate: e.target.value }))} required /></div>
                    <div className="form-field"><label>End Date *</label><input type="date" value={leaveForm.endDate} onChange={(e) => setLeaveForm((prev) => ({ ...prev, endDate: e.target.value }))} required /></div>
                    <div className="form-field" style={{ gridColumn: "span 2" }}><label>Reason</label><input value={leaveForm.reason} onChange={(e) => setLeaveForm((prev) => ({ ...prev, reason: e.target.value }))} /></div>
                    <div className="admin-form-actions">
                      <button type="submit" disabled={savingLeave}>{savingLeave ? "Submitting..." : "Submit Leave"}</button>
                    </div>
                  </form>
                </div>
                <div className="dash-panel">
                  <h3>My Leave History</h3>
                  <div className="dash-table-wrap">
                    <table className="dash-table">
                      <thead><tr><th>Type</th><th>From</th><th>To</th><th>Status</th></tr></thead>
                      <tbody>
                        {leaveRequests.length === 0 && <tr><td colSpan={4}>No leave requests yet.</td></tr>}
                        {leaveRequests.map((row) => (
                          <tr key={row._id}>
                            <td>{row.leaveType}</td>
                            <td>{row.startDate ? new Date(row.startDate).toLocaleDateString() : "-"}</td>
                            <td>{row.endDate ? new Date(row.endDate).toLocaleDateString() : "-"}</td>
                            <td><span className={row.status === "approved" ? "status-pill paid" : row.status === "rejected" ? "status-pill due" : "status-pill pending"}>{row.status}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </section>
          )}

          {activeTab === "notifications" && (
            <section>
              <div className="table-header-row">
                <h2 style={{ margin: 0 }}>Notifications</h2>
                <button className="table-btn" type="button" onClick={markNotificationsRead} disabled={markingNotificationRead || notificationUnreadCount === 0}>
                  {markingNotificationRead ? "Updating..." : "Mark All Read"}
                </button>
              </div>
              <div className="notice-feed">
                {notifications.length === 0 && <p>No notifications yet.</p>}
                {notifications.map((item) => (
                  <article key={item.id} className="notice-feed-item">
                    <h3>{item.title}</h3>
                    <p>{item.message || "-"}</p>
                    <small>
                      {item.createdAt ? new Date(item.createdAt).toLocaleString() : "-"}
                      {item.unread && <span className="dash-badge" style={{ marginLeft: 8 }}>New</span>}
                    </small>
                  </article>
                ))}
              </div>
            </section>
          )}

          {activeTab === "notices" && (
            <section>
              <h2>Notices</h2>
              <div className="notice-feed">
                {notices.length === 0 && <p>No notices yet.</p>}
                {notices.map((notice) => (
                  <article key={notice.id} className="notice-feed-item">
                    <h3>{notice.title}</h3>
                    <p>{notice.details || "-"}</p>
                    <small>
                      {new Date(notice.noticeDate || notice.createdAt).toLocaleDateString()}
                      {toMs(notice.createdAt) > (seenMeta.notices || 0) && (
                        <span className="dash-badge" style={{ marginLeft: 8 }}>New</span>
                      )}
                    </small>
                  </article>
                ))}
              </div>
            </section>
          )}

          {activeTab === "events" && (
            <section>
              <h2>Events</h2>
              <div className="notice-feed">
                {events.length === 0 && <p>No events yet.</p>}
                {events.map((event) => (
                  <article key={event.id} className="notice-feed-item event-feed-item">
                    <h3>{event.title}</h3>
                    <p>{event.description || "-"}</p>
                    <small>
                      Event Date: {event.eventDate ? new Date(event.eventDate).toLocaleDateString() : "-"}
                      {toMs(event.createdAt) > (seenMeta.events || 0) && (
                        <span className="dash-badge" style={{ marginLeft: 8 }}>New</span>
                      )}
                    </small>
                  </article>
                ))}
              </div>
            </section>
          )}
        </main>
      </section>
    </div>
  );
};

export default TeacherDashboard;
